import { LogLevel, log, warn } from "../utils/log";
import { OperatorEvent } from "../interfaces/theos.interface";
import { getTargetOrigin } from "./configuration";
import { processInbound } from "./messageProcessor";

export type EventHandler<T> = (payload: T) => void;

interface HandlerData {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly callback: EventHandler<any>;
    readonly once: boolean;
}

const registeredHandlers: Map<OperatorEvent, HandlerData[]> = new Map<OperatorEvent, HandlerData[]>();

/**
 * @typeParam T Parameter type received by the callback function
 * @param type Event type/name to listen to
 */
export const registerEventHandler = <T = undefined>(type: OperatorEvent): (callback: EventHandler<T>, once?: boolean) => void =>
{
    if (!registeredHandlers.has(type)) {
        registeredHandlers.set(type, []);
    }

    /**
     *
     @param callback Callback funciton
     @param once Remove the listener after it's triggered once
     */
    return (callback: EventHandler<T>, once = false): void =>
    {
        log(`Registering handler for ${type}`, LogLevel.INIT);

        registeredHandlers.get(type)?.push({
            callback,
            once,
        });
    };
};

export const removeEventHandlers = (type: OperatorEvent): void =>
{
    registeredHandlers.delete(type);
};

/**
 * Remove an event handler bound to a specific callback
 * @param type
 * @param callbackToRemove
 */
export const removeEventHandler = (type: OperatorEvent, callbackToRemove: EventHandler<unknown>): void =>
{
    const filteredHandlers = registeredHandlers.get(type)?.filter((handler) =>
    {
        return handler.callback === callbackToRemove;
    });

    if (filteredHandlers !== undefined)
    {
        registeredHandlers.set(type, filteredHandlers);
    }
};

/**
 * Removes all handlers using a callback.
 * @param callbackToRemove - All existing handlers using this callback will be removed
 */
export const removeHandler = (callbackToRemove: EventHandler<unknown>): void =>
{
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    registeredHandlers.forEach((_data, key) =>
    {
        removeEventHandler(key, callbackToRemove);
    });
};

export const handleInboundMessageEvent = (event: MessageEvent): void =>
{
    const operatorOrigin = getTargetOrigin();

    if (operatorOrigin !== "*" && event.origin !== operatorOrigin) {
        return;
    }

    const data = processInbound(event.data);

    if (data === undefined) {
        // The message was not for FEIM
        return;
    }

    log(`🠈 Received ${data.feimOpEvent}`, LogLevel.ALL_MESSAGES, data.payload);

    const filteredHandlers = registeredHandlers.get(data.feimOpEvent)?.filter((handler) =>
    {
        handler.callback(data.payload);

        // Remove once handlers
        return !handler.once;
    });

    if (filteredHandlers) {
        registeredHandlers.set(data.feimOpEvent, filteredHandlers);
    }
    else {
        warn(`Warning! The game has no handlers for event ${data.feimOpEvent}`);
    }
};

/**
 * @internal
 * Directly trigger an event listened by the game
 */
export const triggerEventInGame = (event: OperatorEvent, payload?: {}): void =>
{
    registeredHandlers.get(event)?.forEach((handlerData) =>
    {
        handlerData.callback(payload);
    });
};
